import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { rgba, cover } from 'polished';
import screen from 'superior-mq';
import { bp } from 'Styles/helpers';
import { Link } from '../../routes';
import precedingZero from '../../lib/precendingZero';
import ProjectName from '../project-name/ProjectName';
import Num from '../num';
import RatioImg from '../ratio-img';
import LazyImg from '../lazy-img';
import formatType from '../../lib/formatType';

const ProgramImage = ({ lazyLoad, ...rest }) => (
  lazyLoad
    ? <LazyImg {...rest} />
    : <RatioImg {...rest} />
);

const numStyles = `
  text-align: center;

  ${screen.below(bp.mobileSm, 'display: none;')}
`;

const FeaturedProgramCard = (props) => {
  const { program } = props;

  const route = `/${program.type ? formatType(program.type) : props.parentRoute}/${program.slug}`;

  return (
    <ProgramFigure>
      <Num styles={numStyles}>{precedingZero(props.index + 1)}</Num>
      <Link route={route}>
        <CardLink>
          <ImgWrap
            gridImageWidth={props.gridImageWidth}
            gridImageHeight={props.gridImageHeight}
          >

            {
              /* eslint-disable */
              program.fields.featuredImage && program.fields.featuredImage.photo
                ? <ProgramImage
                    width={468}
                    height={362}
                    src={`${program.fields.featuredImage.photo.file.url}?w=468&h=326&q=70&fm=jpg&fl=progressive&fit=thumb`}
                    alt={program.fields.featuredImage.caption}
                    lazyLoad={props.lazyLoad}
                  />
                : <ProgramImage
                    width={468}
                    height={362}
                    src={`${process.env.S3_ROOT}/w4w-static/images/project-fallback-light.svg`}
                    alt={program.fields.title}
                    lazyLoad={props.lazyLoad}
                  />
              /* eslint-enable */
            }

            {program.fields.goal &&
              <Goal>
                Goal {program.fields.goal.formatted}
                <Currency> {program.settlementCurrency}</Currency>
              </Goal>
            }

            <MobileDonate>Donate</MobileDonate>

            {program.fields.goal && program.fundsRaised &&
              <Progress
                goal={program.fields.goal.raw}
                progress={program.fundsRaised.raw}
              />
            }

          </ImgWrap>

          <FigCap>
            <div>
              <ProjectName color={props.titleColor}>{program.fields.title}</ProjectName>
              <ProjectLocation>{program.fields.location}</ProjectLocation>
            </div>

            {props.showDonateButton &&
              <span>Donate</span>
            }
          </FigCap>
        </CardLink>
      </Link>
    </ProgramFigure>
  );
};

const ProgramFigure = styled.figure`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Currency = styled.em`
  font-size: .75rem;
  font-style: normal;
`;

const ImgWrap = styled.div`
  position: relative;
  overflow: hidden;
  font-size: 0;

  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 55%;
    background: linear-gradient(0deg, ${rgba('#000', 0.6)}, ${rgba('#000', 0)});
    z-index: 0;
  }
`;

const Progress = styled.div`
  --completion: ${props => props.progress / props.goal > 1 ? 1 : props.progress / props.goal};
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 4px;
  background-color: ${props => rgba(props.theme.blue, 0.38)};
  z-index: 1;

  &::after {
    ${cover()}
    content: "";
    transform: scaleX(var(--completion));
    transform-origin: left;
    background-color: ${props => props.theme.blue};
  }
`;

const Goal = styled.div`
  color: ${props => props.theme.white};
  position: absolute;
  left: 28px;
  bottom: 20px;
  font-size: 1rem;
  line-height: 1;
  z-index: 1;

  ${screen.below(bp.mobileSm, `
    left: 20px;
  `)}
`;

const MobileDonate = styled(Goal)`
  right: 20px;
  color: ${props => props.theme.blue};

  ${screen.above(bp.mobileSm, `
    display: none;
  `)}

  ${screen.below(bp.mobileSm, `
    left: auto;
  `)}
`;

const FigCap = styled.figcaption`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  padding: 28px 28px 0;

  ${screen.below(bp.desktop, `
    padding: 20px 20px 0;
  `)}

  ${screen.below(bp.laptopSm, `
    padding: 20px 10px 0;
  `)}

  ${screen.below(bp.laptopSm, `
    padding-top: 16px;
  `)}

  span {
    font-size: 1rem;
    color: ${props => props.theme.blue};

    ${screen.below(bp.mobileSm, `
      display: none;
    `)}
  }
`;

const ProjectLocation = styled.p`
  font-size: 1rem;
  margin: 0 0 16px;
  color: #646868;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${screen.below(bp.portrait, 'margin: 0 0 8px;')}
`;

const CardLink = styled.a`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  text-decoration: none;
  position: relative;
  color: #fff;
`;

FeaturedProgramCard.propTypes = {
  program: PropTypes.object,
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showDonateButton: PropTypes.bool,
  parentRoute: PropTypes.string,
  lazyLoad: PropTypes.bool,
};

FeaturedProgramCard.defaultProps = {
  showDonateButton: false,
  parentRoute: 'programs',
};

export default FeaturedProgramCard;
